import React, { useEffect, useState } from "react";
import { useGetVolumeBreakdownsMutation } from "../../../store/api/lumi/volume";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import classes from "./MetadataConceptVolume.module.css";
import Scrollbars from "react-custom-scrollbars-2";
import { FilterNameSelector } from "../../DashboardComponents/DashboardFilters/shared";
import SmallMultipleChart from "./SmallMultipleChart";
import LabelSelector from "./LabelSelector";

const MetadataConceptVolume = (props) => {
  const { filters, metaDataQu, breakdownName, setBreakdownName } = props;
  const metaData = metaDataQu?.metaDataWithStringType || {};
  const [getTotals] = useGetVolumeBreakdownsMutation();
  const [loading, setLoading] = useState(false);
  const [processedData, setProcessedData] = useState([]);
  
  // State for which labels (bars) to show.
  const [selectedLabels, setSelectedLabels] = useState(["Exact Matches", "Conceptual Matches"]);
  const toggleLabel = (label) => {
    if (selectedLabels.includes(label)) {
      setSelectedLabels(selectedLabels.filter(l => l !== label));
    } else {
      setSelectedLabels([...selectedLabels, label]);
    }
  };

  async function getVolumeData() {
    setLoading(true);
    const conceptFilter = getConceptFilter(props);
    const { data: dataConcepts } = await getTotals({
      id: props.id,
      concept_selector: conceptFilter,
      filter: props.filters,
      breakdowns: [
        { name: Array.isArray(breakdownName) ? breakdownName[0] : breakdownName },
      ],
    });
    props.setFilterDocCount(dataConcepts.filter_count);
    if (props.conceptList) {
      // Optionally, set the local concept options (if needed)
      props.setLocalConceptOptions(dataConcepts.match_counts);
    }
    const breakdownData = dataConcepts.breakdowns[0].buckets;

    const aggregatedData = breakdownData
      .filter(({ match_counts }) => !match_counts.every(({ match_count }) => match_count === 0))
      .map(({ label, match_counts }) => {
        // Sort match_counts by match_count descending and then slice the top 5 concepts.
        const sortedMatchCounts = [...match_counts].sort((a, b) => b.match_count - a.match_count);
        const topMatchCounts = sortedMatchCounts.slice(0, 5);
        const chartData = topMatchCounts.map(({ name, match_count, exact_match_count }) => ({
          concept: name,
          exact: exact_match_count,
          conceptual: match_count - exact_match_count,
        }));
        return {
          location: label,
          chartData,
        };
      })
      // Sort buckets by total volume (exact + conceptual) in descending order.
      .sort((a, b) =>
        b.chartData.reduce((sum, d) => sum + d.exact + d.conceptual, 0) -
        a.chartData.reduce((sum, d) => sum + d.exact + d.conceptual, 0)
      )
      .slice(0, 100);

    // Calculate a global maximum (across all buckets) for the Y axis.
    let globalMax = 0;
    aggregatedData.forEach((group) => {
      group.chartData.forEach((item) => {
        const total = (item.exact || 0) + (item.conceptual || 0);
        if (total > globalMax) globalMax = total;
      });
    });

    // Attach the same globalMax to every group so each chart uses the same Y-axis scale.
    const processed = aggregatedData.map(group => ({
      ...group,
      globalMax,
    }));

    setProcessedData(processed);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    const debouncedGetVolumes = debounce(getVolumeData, 500);
    debouncedGetVolumes();
    return () => {
      debouncedGetVolumes.cancel();
    };
  }, [filters, breakdownName, props.conceptTypeDay, props.selectedLocalConcepts, props.conceptList]);

  useEffect(() => {
    props.setAdditionalFilters([{
      key: "Special",
      label: "Select Breakdown Variable:",
      showArrow: false,
      children: (
        <FilterNameSelector
          selectedFieldName={breakdownName}
          setSelectedFieldName={setBreakdownName}
          metaData={metaData}
        />
      ),
    }]);
  }, [breakdownName, metaData]);

  if (loading) {
    return (
      <Spin
        className="spinner-by-center"
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      />
    );
  }

  return (
    <div
      className={classes.widgetContainer}
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px", padding: "8px" }}>
        <LabelSelector
          labels={["Exact Matches", "Conceptual Matches"]}
          selectedLabels={selectedLabels}
          onToggleLabel={toggleLabel}
        />
      </div>
      {/* Scrollable area: grid of charts */}
      <div style={{ flex: 1 }}>
        <Scrollbars style={{ height: "100%" }}>
          {processedData.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(356px, 1fr))",
                gap: "1.5rem",
                padding: "1rem"
              }}
            >
              {processedData.map((group, index) => (
                <SmallMultipleChart
                  key={`group-${index}`}
                  location={group.location}
                  data={group.chartData}
                  showExact={selectedLabels.includes("Exact Matches")}
                  showConceptual={selectedLabels.includes("Conceptual Matches")}
                  // Fixed Y‑axis domain: min is 0 and max is the global maximum across all groups.
                  minYAxis={0}
                  maxYAxis={group.globalMax}
                />
              ))}
            </div>
          ) : (
            <div>No data available</div>
          )}
        </Scrollbars>
      </div>
    </div>
  );
};

export default MetadataConceptVolume;
